/**
 * Cross-project work lists: GET /me/work (tasks assigned to the caller) and
 * GET /me/reviews (review-state tasks the caller may see). Both are scoped by
 * the server from the caller's claims and paged with a truthful meta.
 * Field names match backend tasks.WorkItem / tasks.ReviewItem json tags.
 */
import { apiClient } from './index';
import type { TaskPriority, TaskStatus } from './pm-api';

export interface WorkItem {
  id: string;
  title: string;
  status: TaskStatus;
  priority: TaskPriority;
  due_date?: string;
  project_id: string;
  project_name: string;
  /** Prerequisites of this task that are not done (they may be hidden from the viewer). */
  open_blockers: number;
}

export interface ReviewItem extends WorkItem {
  /** Body of the task's latest llm_summary comment, when there is one. */
  handoff_summary?: string;
}

export interface WorkPageMeta {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface WorkPage<T> {
  data: T[];
  meta: WorkPageMeta;
}

export const WORK_PAGE_SIZE = 25;

async function getPage<T>(path: string, page: number, limit: number): Promise<WorkPage<T>> {
  const { data } = await apiClient.get<WorkPage<T>>(path, { params: { page, limit } });
  return { data: data.data ?? [], meta: data.meta };
}

export const getMyWork = (page: number, limit = WORK_PAGE_SIZE) => getPage<WorkItem>('/me/work', page, limit);
export const getReviews = (page: number, limit = WORK_PAGE_SIZE) => getPage<ReviewItem>('/me/reviews', page, limit);

/**
 * GET /me/agent-comment-count: live comments posted over an API key or OAuth
 * connector (server-recorded auth_channel) on tasks the caller may see.
 */
export async function getAgentCommentCount(): Promise<number> {
  const { data } = await apiClient.get<{ count: number }>('/me/agent-comment-count');
  return data.count;
}
